<template>
    <RaterChrome :max-width="640" :show-anonymous="!invite?.is_authenticated">
        <div v-if="loading" class="body-small text-valence-stone-500 pt-20">Loading…</div>
        <BaseNotification v-else-if="loadError" theme="error" :text="loadError" hide-dismiss class="mt-12" />
        <template v-else-if="submitted">
            <div class="celebrate">
                <div class="celebrate__badgewrap">
                    <span class="confetti" style="background: #9ed18a; top: 10px; left: 40px; transform: rotate(18deg)" aria-hidden="true"></span>
                    <span class="confetti confetti--dot" style="background: #81dada; top: 26px; left: 78px" aria-hidden="true"></span>
                    <span class="confetti" style="background: #9ed18a; top: 2px; left: 112px; transform: rotate(-14deg)" aria-hidden="true"></span>
                    <span class="confetti confetti--dot" style="background: #bee59f; top: 16px; right: 30px" aria-hidden="true"></span>
                    <span class="confetti" style="background: #9ed18a; top: 46px; right: 14px; transform: rotate(24deg)" aria-hidden="true"></span>
                    <span class="confetti confetti--dot" style="background: #81dada; bottom: 16px; left: 34px" aria-hidden="true"></span>
                    <span class="confetti" style="background: #9ed18a; bottom: 12px; right: 46px; transform: rotate(-22deg)" aria-hidden="true"></span>
                    <div class="celebrate__badge" aria-hidden="true">
                        <svg viewBox="0 0 24 18" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M2 9.5 L8.5 16 L22 2" />
                        </svg>
                    </div>
                </div>
                <h1 class="display-serif text-valence-grey-850 leading-tight">Thanks for your feedback</h1>
                <p class="body-small text-valence-stone-500 max-w-md">
                    Your answers are anonymous and will be combined with others' to help
                    <template v-if="subjectFirstName">{{ subjectFirstName }}</template>
                    <template v-else>them</template>
                    grow.
                </p>
                <div class="celebrate__actions">
                    <a :href="closeHref" class="base-btn-neutral base-btn-square base-btn-sm inline-flex items-center gap-2"> Close </a>
                </div>
            </div>
        </template>

        <template v-else-if="invite">
            <BaseAvatar size="xl" theme="neutral" class="mb-8"><i class="bi bi-hourglass-split text-3xl" aria-hidden="true"></i></BaseAvatar>
            <h1 class="display-serif text-valence-grey-850 leading-tight mb-3">This 360 invite isn't complete yet.</h1>
            <p class="body text-valence-stone-500 max-w-md mb-8">Pick up where you left off — your in-progress answers were saved.</p>
            <a :href="resumeHref" class="base-btn-primary inline-flex items-center gap-2"> Resume <i class="bi bi-arrow-right" aria-hidden="true"></i> </a>
        </template>
    </RaterChrome>
</template>

<script setup lang="ts">
import BaseAvatar from "~vue/components/BaseAvatar.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import RaterChrome from "~vue/components/RaterChrome.vue";
import { useInviteLoader } from "~vue/composables/useInviteLoader";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { markPagePublic } from "~vue/utils/sessionEnded";
import { computed, watch } from "vue";

interface Props {
    invite_token: string;
    is_anonymous: boolean;
}

const props = defineProps<Props>();

// An external rater has no account to log back into, so a rejected token there must stay silent.
// An internal rater is signed in, and a real session expiry must still offer them recovery.
if (props.is_anonymous) {
    markPagePublic();
}

const { invite, loading, loadError } = useInviteLoader(props.invite_token);

watch(invite, (loaded) => {
    if (!loaded) {
        return;
    }
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_RATER_DONE_VIEWED, {
        submitted: loaded.state === "responded",
        min_responses: loaded.cycle_min_responses,
    });
});

const subjectFirstName = computed(() => invite.value?.subject_first_name ?? "");
const submitted = computed(() => invite.value?.state === "responded");
const resumeHref = computed(() => `/reflections/360/r/${props.invite_token}/`);
// External raters (no Nadia account) land back on the marketing site; signed-in
// users go home. Preserves the historical navigation target for both branches.
const closeHref = computed(() => (invite.value?.is_authenticated ? "/" : "https://valence.co"));
</script>

<style scoped>
@reference "../../../css/tailwind.css";

.celebrate {
    @apply flex flex-col items-center text-center gap-3.5;
    padding: 40px 0 8px;
}

.celebrate__badgewrap {
    @apply relative flex items-center justify-center;
    width: 220px;
    height: 120px;
    margin-bottom: 2px;
}

.celebrate__badge {
    @apply flex items-center justify-center text-white;
    width: 84px;
    height: 84px;
    border-radius: 50%;
    background: #5fa37c;
    animation: celebratePop 0.45s ease-out both;
}

.celebrate__badge svg {
    width: 38px;
    height: 28px;
}

.confetti {
    @apply absolute;
    width: 9px;
    height: 9px;
    border-radius: 2px;
    animation: confettiIn 0.5s ease-out both;
}

.confetti--dot {
    border-radius: 50%;
}

.celebrate__actions {
    @apply flex gap-2.5;
    margin-top: 18px;
}

@keyframes celebratePop {
    0% {
        transform: scale(0.6);
        opacity: 0;
    }
    60% {
        transform: scale(1.08);
    }
    100% {
        transform: scale(1);
        opacity: 1;
    }
}

@keyframes confettiIn {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}

@media (prefers-reduced-motion: reduce) {
    .celebrate__badge,
    .confetti {
        animation: none;
    }
}
</style>
